import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Droplets,
  Plus,
  RefreshCcw,
  ShieldCheck,
  Wifi,
} from 'lucide-react';
import { BaseLayout } from '@/shared/components/layout';
import { useUser } from '@/shared/hooks/useUser';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Checkbox } from '@/shared/ui/checkbox';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Progress } from '@/shared/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs';
import CustomCombobox from '@/shared/ui/custom/combobox';
import { cn } from '@/lib/utils';
import { ClientSelector, ProjectSelector } from '@/features/add-scan/components';
import { useClients, useProjects } from '@/features/add-scan/hooks';
import { useProjectSummary } from '@/features/project-dashboard/hooks/useProjectSummary';
import {
  useSmartValveAreaTags,
  useSmartValveChoices,
  useSmartValveDevices,
  useSmartValveEvents,
  useSmartValveHubs,
  useSmartValveMutations,
  useSmartValveSummary,
  useSmartValveUnits,
  useSmartValveZones,
  useYoLinkAccounts,
} from '../hooks/useSmartValves';
import type {
  CommissioningTask,
  CreateSmartValveDeviceData,
  SmartValveAreaTag,
  SmartValveDevice,
  SmartValveHub,
  SmartValveUnit,
  SmartValveZone,
} from '../types';

type OnboardingFormState = {
  client: string;
  project: string;
  zone: string;
  zoneName: string;
  rfZoneLabel: string;
  yolinkAccount: string;
  yolinkAccountName: string;
  hubName: string;
  hubDeviceId: string;
  yolinkUaid: string;
  yolinkSecret: string;
  areaTagId: string;
  defaultHotspotId: string;
  installLocation: string;
  flowSmartLabel: string;
  flowSmartDeviceId: string;
  notes: string;
};

const emptyForm: OnboardingFormState = {
  client: '',
  project: '',
  zone: 'new',
  zoneName: 'Main RF zone',
  rfZoneLabel: 'RF-1',
  yolinkAccount: 'new',
  yolinkAccountName: '',
  hubName: '',
  hubDeviceId: '',
  yolinkUaid: '',
  yolinkSecret: '',
  areaTagId: '',
  defaultHotspotId: '',
  installLocation: '',
  flowSmartLabel: '',
  flowSmartDeviceId: '',
  notes: '',
};

const statusVariant = (
  status: string
): 'default' | 'secondary' | 'success' | 'destructive' | 'outline' => {
  if (['active', 'online', 'closed', 'dry', 'pass'].includes(status)) return 'success';
  if (['leak', 'error', 'offline', 'service_needed', 'fail'].includes(status)) return 'destructive';
  if (['testing', 'open', 'low_battery'].includes(status)) return 'default';
  return 'secondary';
};

const formatDateTime = (value: string | null | undefined) => {
  if (!value) return 'Not recorded';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));
};

const completionPercent = (unit: SmartValveUnit) => {
  if (!unit.total_task_count) return 0;
  return Math.round((unit.completed_task_count / unit.total_task_count) * 100);
};

function StatChip({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string; strokeWidth?: string | number }>;
  label: string;
  value: string | number;
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-3 p-4">
        <span className="grid size-10 shrink-0 place-items-center rounded-[11px] border border-border bg-muted text-primary">
          <Icon className="size-5" strokeWidth={1.9} />
        </span>
        <div className="min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
            {label}
          </div>
          <div className="font-display text-2xl font-extrabold tracking-[-0.02em]">{value}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

function OnboardingPanel({
  zones,
  defaultClientId,
  defaultProjectId,
  onSubmit,
  isSubmitting,
}: {
  zones: SmartValveZone[];
  defaultClientId?: number;
  defaultProjectId?: number;
  onSubmit: (form: OnboardingFormState, areaTag: SmartValveAreaTag) => Promise<void>;
  isSubmitting: boolean;
}) {
  const { user } = useUser();
  const [form, setForm] = useState<OnboardingFormState>(emptyForm);
  const { clients } = useClients(Boolean(user?.is_staff || user?.is_superuser));
  const { projects, isLoading: isLoadingProjects } = useProjects(form.client);
  const selectedProjectId = form.project ? Number(form.project) : undefined;
  const areaTags = useSmartValveAreaTags(selectedProjectId);
  const yolinkAccounts = useYoLinkAccounts(selectedProjectId);

  const setValue = (key: keyof OnboardingFormState, value: string) =>
    setForm((current) => ({ ...current, [key]: value }));

  useEffect(() => {
    setForm((current) => ({
      ...current,
      client: current.client || (defaultClientId ? String(defaultClientId) : ''),
      project: current.project || (defaultProjectId ? String(defaultProjectId) : ''),
    }));
  }, [defaultClientId, defaultProjectId]);

  const projectZones = useMemo(
    () => zones.filter((zone) => !selectedProjectId || zone.project === selectedProjectId),
    [selectedProjectId, zones]
  );
  const areaTagOptions = useMemo(
    () =>
      (areaTags.data ?? []).map((tag) => ({
        value: String(tag.id),
        label: tag.layout_name ? `${tag.name} (${tag.layout_name})` : tag.name,
      })),
    [areaTags.data]
  );
  const selectedAreaTag = useMemo(
    () => (areaTags.data ?? []).find((tag) => String(tag.id) === form.areaTagId),
    [areaTags.data, form.areaTagId]
  );
  const accountOptions = useMemo(
    () =>
      (yolinkAccounts.data ?? []).map((account) => ({
        value: String(account.id),
        label: account.name,
      })),
    [yolinkAccounts.data]
  );
  const hasZoneAccount =
    form.zone !== 'new' ||
    (form.yolinkAccount !== 'new'
      ? Boolean(form.yolinkAccount)
      : Boolean(form.yolinkAccountName && form.yolinkUaid && form.yolinkSecret));

  const canSubmit = Boolean(
    form.client &&
      form.project &&
      form.areaTagId &&
      form.flowSmartLabel &&
      selectedAreaTag &&
      (form.zone !== 'new' || (form.zoneName && form.hubName && hasZoneAccount))
  );

  const handleClientChange = (value: string) => {
    setForm((current) => ({
      ...current,
      client: value,
      project: '',
      areaTagId: '',
      zone: 'new',
      yolinkAccount: 'new',
    }));
  };

  const handleProjectChange = (value: string) => {
    setForm((current) => ({
      ...current,
      project: value,
      areaTagId: '',
      zone: 'new',
      yolinkAccount: 'new',
    }));
  };

  useEffect(() => {
    if (
      form.zone !== 'new' ||
      form.yolinkAccount !== 'new' ||
      form.yolinkUaid ||
      form.yolinkSecret
    ) {
      return;
    }
    const firstAccount = yolinkAccounts.data?.[0];
    if (firstAccount) {
      setValue('yolinkAccount', String(firstAccount.id));
    }
  }, [form.zone, form.yolinkAccount, form.yolinkSecret, form.yolinkUaid, yolinkAccounts.data]);

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
              Install onboarding
            </div>
            <CardTitle className="mt-1 text-xl">New unit setup</CardTitle>
          </div>
          <Badge variant="outline">Hub + FlowSmart</Badge>
        </div>
      </CardHeader>
      <CardContent>
        <form
          className="space-y-6"
          onSubmit={(event) => {
            event.preventDefault();
            if (canSubmit && selectedAreaTag) void onSubmit(form, selectedAreaTag);
          }}
        >
          <div className="grid gap-4 md:grid-cols-4">
            <div className="md:col-span-2">
              <ClientSelector clients={clients} value={form.client} onChange={handleClientChange} />
            </div>
            <div className="md:col-span-2">
              <ProjectSelector
                projects={projects}
                value={form.project}
                onChange={handleProjectChange}
                disabled={!form.client}
                isLoading={isLoadingProjects}
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Unit</Label>
              <CustomCombobox
                options={areaTagOptions}
                value={form.areaTagId}
                onChange={(value) => setValue('areaTagId', value)}
                placeholder={
                  form.project
                    ? areaTags.isLoading
                      ? 'Loading units...'
                      : 'Select unit'
                    : 'Select a project first'
                }
                searchPlaceholder="Search units..."
                emptyMessage="No units/locations found."
                disabled={!form.project || areaTags.isLoading}
                showAddNew={false}
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>RF zone</Label>
              <Select
                value={form.zone}
                onValueChange={(value) => setValue('zone', value)}
                disabled={!form.project}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">Create new RF zone</SelectItem>
                  {projectZones.map((zone) => (
                    <SelectItem key={zone.id} value={String(zone.id)}>
                      {zone.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {form.zone === 'new' && (
            <div className="grid gap-4 rounded-[13px] border border-border bg-muted/40 p-4 md:grid-cols-3">
              <Field
                label="Zone name"
                value={form.zoneName}
                onChange={(value) => setValue('zoneName', value)}
              />
              <Field
                label="RF label"
                value={form.rfZoneLabel}
                onChange={(value) => setValue('rfZoneLabel', value)}
              />
              <div className="space-y-2">
                <Label>YoLink account</Label>
                <Select
                  value={form.yolinkAccount}
                  onValueChange={(value) => setValue('yolinkAccount', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">Create new YoLink account</SelectItem>
                    {accountOptions.map((account) => (
                      <SelectItem key={account.value} value={account.value}>
                        {account.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {form.yolinkAccount === 'new' && (
                <>
                  <Field
                    label="Account name"
                    value={form.yolinkAccountName}
                    onChange={(value) => setValue('yolinkAccountName', value)}
                    placeholder="Building YoLink account"
                  />
                  <Field
                    label="YoLink UAID"
                    value={form.yolinkUaid}
                    onChange={(value) => setValue('yolinkUaid', value)}
                  />
                  <Field
                    label="YoLink secret"
                    type="password"
                    value={form.yolinkSecret}
                    onChange={(value) => setValue('yolinkSecret', value)}
                  />
                </>
              )}
              <Field
                label="Hub name"
                value={form.hubName}
                onChange={(value) => setValue('hubName', value)}
              />
              <Field
                label="Hub device ID"
                value={form.hubDeviceId}
                onChange={(value) => setValue('hubDeviceId', value)}
              />
            </div>
          )}

          <div className="grid gap-4 md:grid-cols-4">
            <Field
              label="Hotspot ID"
              type="number"
              value={form.defaultHotspotId}
              onChange={(value) => setValue('defaultHotspotId', value)}
              placeholder="Optional device location"
            />
            <Field
              label="Install location"
              value={form.installLocation}
              onChange={(value) => setValue('installLocation', value)}
              placeholder="Mechanical room"
            />
            <Field
              label="FlowSmart label"
              value={form.flowSmartLabel}
              onChange={(value) => setValue('flowSmartLabel', value)}
              placeholder="FS-001"
            />
            <Field
              label="FlowSmart device ID"
              value={form.flowSmartDeviceId}
              onChange={(value) => setValue('flowSmartDeviceId', value)}
              placeholder="Optional YoLink ID"
            />
            <div className="md:col-span-2">
              <Field
                label="Notes"
                value={form.notes}
                onChange={(value) => setValue('notes', value)}
              />
            </div>
          </div>

          <Button type="submit" disabled={!canSubmit || isSubmitting}>
            <Plus className="mr-2 size-4" strokeWidth={1.9} />
            {isSubmitting ? 'Creating setup...' : 'Onboard unit'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function UnitChecklist({
  unit,
  onTaskToggle,
  isUpdating,
}: {
  unit: SmartValveUnit;
  onTaskToggle: (task: CommissioningTask, checked: boolean) => void;
  isUpdating: boolean;
}) {
  const progress = completionPercent(unit);
  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-xl">{unit.unit_label}</CardTitle>
            <div className="mt-2 flex flex-wrap gap-2 text-sm text-muted-foreground">
              <span>{unit.zone_name}</span>
              <span>{unit.area_tag_name}</span>
              {unit.install_location && <span>{unit.install_location}</span>}
            </div>
          </div>
          <Badge variant={statusVariant(unit.status)}>{unit.status_label}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="flex justify-between text-sm font-semibold">
            <span>Commissioning</span>
            <span>{progress}%</span>
          </div>
          <Progress value={progress} />
        </div>
        <div className="space-y-2">
          {unit.commissioning_tasks.map((task) => (
            <label
              key={task.id}
              className="flex items-center gap-3 rounded-[11px] border border-border bg-muted/40 px-3 py-2.5 text-sm"
            >
              <Checkbox
                checked={task.is_complete}
                disabled={isUpdating}
                onCheckedChange={(checked) => onTaskToggle(task, Boolean(checked))}
              />
              <span
                className={cn('flex-1', task.is_complete && 'text-muted-foreground line-through')}
              >
                {task.task_type_label}
              </span>
              <span className="text-xs font-semibold text-muted-foreground">
                {task.completed_quantity}/{task.target_quantity}
              </span>
            </label>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function DeviceTable({
  devices,
  onRefresh,
  onOpen,
  onClose,
  isBusy,
}: {
  devices: SmartValveDevice[];
  onRefresh: (id: number) => void;
  onOpen: (id: number) => void;
  onClose: (id: number) => void;
  isBusy: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-xl">Device status</CardTitle>
          <Badge variant="secondary">{devices.length} devices</Badge>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Device</TableHead>
              <TableHead>Unit</TableHead>
              <TableHead>Hotspot</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Battery</TableHead>
              <TableHead>Last seen</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {devices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground">
                  No devices onboarded yet.
                </TableCell>
              </TableRow>
            ) : (
              devices.map((device) =>
                (() => {
                  const refreshAction = device.available_actions.find(
                    (action) => action.key === 'refresh_status'
                  );
                  const openAction = device.available_actions.find(
                    (action) => action.key === 'open_valve'
                  );
                  const closeAction = device.available_actions.find(
                    (action) => action.key === 'close_valve'
                  );
                  return (
                    <TableRow key={device.id}>
                      <TableCell>
                        <div className="font-semibold">{device.display_name}</div>
                        <div className="text-xs text-muted-foreground">
                          {device.device_type_label}
                          {device.flowsmart_profile?.flow_rate_gpm && (
                            <span> · {device.flowsmart_profile.flow_rate_gpm} gpm</span>
                          )}
                          {device.flowsmart_profile?.total_gallons && (
                            <span> · {device.flowsmart_profile.total_gallons} gal</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{device.unit_label || 'Unmapped'}</TableCell>
                      <TableCell>{device.hotspot_title || 'Not mapped'}</TableCell>
                      <TableCell>
                        <Badge variant={statusVariant(device.status)}>{device.status_label}</Badge>
                      </TableCell>
                      <TableCell>
                        {device.battery === null ? 'N/A' : `${device.battery}%`}
                      </TableCell>
                      <TableCell>{formatDateTime(device.last_seen_at)}</TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-2">
                          {refreshAction && (
                            <Button
                              size="icon"
                              variant="outline"
                              disabled={isBusy || !refreshAction.enabled}
                              onClick={() => onRefresh(device.id)}
                              title={
                                refreshAction.enabled
                                  ? 'Refresh status'
                                  : refreshAction.disabled_reason
                              }
                            >
                              <RefreshCcw className="size-4" strokeWidth={1.9} />
                            </Button>
                          )}
                          {openAction && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={isBusy || !openAction.enabled}
                              onClick={() => onOpen(device.id)}
                              title={openAction.disabled_reason}
                            >
                              Open
                            </Button>
                          )}
                          {closeAction && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={isBusy || !closeAction.enabled}
                              onClick={() => onClose(device.id)}
                              title={closeAction.disabled_reason}
                            >
                              <ShieldCheck className="mr-2 size-4" strokeWidth={1.9} />
                              Close
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })()
              )
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function HubTable({
  hubs,
  onRefresh,
  isBusy,
}: {
  hubs: SmartValveHub[];
  onRefresh: (id: number) => void;
  isBusy: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-xl">Hub status</CardTitle>
          <Badge variant="secondary">{hubs.length} hubs</Badge>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Hub</TableHead>
              <TableHead>RF zone</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Last seen</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {hubs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground">
                  No hubs onboarded yet.
                </TableCell>
              </TableRow>
            ) : (
              hubs.map((hub) => (
                <TableRow key={hub.id}>
                  <TableCell>
                    <div className="font-semibold">{hub.display_name}</div>
                    <div className="text-xs text-muted-foreground">
                      {hub.yolink_device_id || hub.model || 'YoLink hub'}
                    </div>
                  </TableCell>
                  <TableCell>{hub.zone_name}</TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(hub.status)}>{hub.status}</Badge>
                  </TableCell>
                  <TableCell>{formatDateTime(hub.last_seen_at)}</TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-2">
                      {hub.available_actions.some((action) => action.key === 'sync_zone') && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={isBusy}
                          onClick={() => onRefresh(hub.id)}
                        >
                          <RefreshCcw className="mr-2 size-4" strokeWidth={1.9} />
                          Sync
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export function SmartValvesPage() {
  const { projectId: routeProjectId } = useParams();
  const projectId = routeProjectId ? Number(routeProjectId) : undefined;
  const projectSummary = useProjectSummary(projectId);
  const defaultClientId = projectSummary.data?.project.client?.id;
  const summary = useSmartValveSummary(projectId);
  const zones = useSmartValveZones(projectId);
  const hubs = useSmartValveHubs(projectId);
  const units = useSmartValveUnits(projectId);
  const devices = useSmartValveDevices(projectId);
  const events = useSmartValveEvents(projectId);
  useSmartValveChoices();
  const mutations = useSmartValveMutations(projectId);
  const [notice, setNotice] = useState<string>('');

  const projectLabel = projectSummary.data?.project.name || 'Smart water valves';
  const stats = summary.data ?? {
    zones: 0,
    units: 0,
    active_units: 0,
    hubs: 0,
    devices: 0,
    flowsmarts: 0,
    valves: 0,
    leak_sensors: 0,
    open_alerts: 0,
    completed_tasks: 0,
    total_tasks: 0,
  };
  const taskProgress = stats.total_tasks
    ? Math.round((stats.completed_tasks / stats.total_tasks) * 100)
    : 0;

  const sortedDevices = useMemo(
    () => [...(devices.data ?? [])].sort((a, b) => a.display_name.localeCompare(b.display_name)),
    [devices.data]
  );
  const sortedHubs = useMemo(
    () => [...(hubs.data ?? [])].sort((a, b) => a.display_name.localeCompare(b.display_name)),
    [hubs.data]
  );

  const busy =
    mutations.createAccount.isPending ||
    mutations.createZone.isPending ||
    mutations.createHub.isPending ||
    mutations.createUnit.isPending ||
    mutations.createDevice.isPending ||
    mutations.syncZone.isPending ||
    mutations.refreshHub.isPending ||
    mutations.refreshDevice.isPending ||
    mutations.openValve.isPending ||
    mutations.closeValve.isPending ||
    mutations.updateTask.isPending;

  const handleOnboard = async (form: OnboardingFormState, areaTag: SmartValveAreaTag) => {
    setNotice('');
    const client = Number(form.client);
    const project = Number(form.project);
    let zoneId = form.zone === 'new' ? undefined : Number(form.zone);

    if (!zoneId) {
      let yolinkAccountId = form.yolinkAccount === 'new' ? undefined : Number(form.yolinkAccount);
      if (!yolinkAccountId) {
        const account = await mutations.createAccount.mutateAsync({
          client,
          project,
          name: form.yolinkAccountName,
          yolink_uaid: form.yolinkUaid,
          yolink_secret: form.yolinkSecret,
          notes: form.notes,
        });
        yolinkAccountId = account.id;
      }

      const createdZone = await mutations.createZone.mutateAsync({
        client,
        project,
        yolink_account: yolinkAccountId,
        name: form.zoneName,
        rf_zone_label: form.rfZoneLabel,
        notes: form.notes,
      });
      zoneId = createdZone.id;

      if (form.hubName || form.hubDeviceId) {
        await mutations.createHub.mutateAsync({
          zone: zoneId,
          display_name: form.hubName || `${form.rfZoneLabel || form.zoneName} hub`,
          yolink_device_id: form.hubDeviceId,
          model: 'YoLink hub',
          install_location: form.installLocation,
        });
      }
    }

    const unit = await mutations.createUnit.mutateAsync({
      client,
      project,
      zone: zoneId,
      area_tag: areaTag.id,
      unit_label: areaTag.name,
      install_location: form.installLocation,
      notes: form.notes,
    });
    const defaultHotspot = form.defaultHotspotId ? Number(form.defaultHotspotId) : null;

    const devicePayloads: CreateSmartValveDeviceData[] = [
      form.flowSmartLabel && {
        zone: zoneId,
        unit: unit.id,
        hotspot: defaultHotspot,
        device_type: 'flowsmart',
        display_name: `${unit.unit_label} ${form.flowSmartLabel}`,
        label_code: form.flowSmartLabel,
        yolink_device_id: form.flowSmartDeviceId,
        install_location: form.installLocation,
        model: 'YoLink FlowSmart All-in-One',
      },
    ].filter(Boolean) as CreateSmartValveDeviceData[];

    await Promise.all(devicePayloads.map((payload) => mutations.createDevice.mutateAsync(payload)));
    setNotice(`Onboarded ${unit.unit_label} with ${devicePayloads.length} device records.`);
  };

  return (
    <BaseLayout
      isLoading={summary.isLoading && zones.isLoading && units.isLoading}
      loadingMessage="Loading smart valve console..."
    >
      <div className="mx-auto w-full max-w-[1600px] space-y-7 px-4 py-6 md:px-9 md:py-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-[13.5px] font-semibold text-muted-foreground">{projectLabel}</div>
            <h1 className="font-display text-3xl font-extrabold tracking-[-0.02em] md:text-[2.4rem]">
              Smart Valve Shutoff
            </h1>
          </div>
          <div className="flex flex-wrap gap-2">
            {(zones.data ?? []).map((zone) => (
              <Button
                key={zone.id}
                variant="outline"
                disabled={busy || !zone.has_yolink_credentials}
                onClick={() =>
                  void mutations.syncZone
                    .mutateAsync(zone.id)
                    .then((result) =>
                      setNotice(
                        `Synced ${result.created + result.updated} devices from ${zone.name}.`
                      )
                    )
                }
              >
                <RefreshCcw className="mr-2 size-4" strokeWidth={1.9} />
                Sync {zone.rf_zone_label || zone.name}
              </Button>
            ))}
          </div>
        </div>

        {notice && (
          <div className="rounded-[13px] border border-primary/25 bg-primary/10 px-4 py-3 text-sm font-semibold text-foreground">
            {notice}
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          <StatChip icon={Wifi} label="RF zones" value={stats.zones} />
          <StatChip icon={ShieldCheck} label="Units" value={stats.units} />
          <StatChip icon={Wifi} label="Hubs" value={stats.hubs} />
          <StatChip icon={Droplets} label="FlowSmart" value={stats.flowsmarts} />
          <StatChip icon={AlertTriangle} label="Open alerts" value={stats.open_alerts} />
        </div>

        <Card>
          <CardContent className="grid gap-4 p-5 md:grid-cols-[1fr_auto] md:items-center">
            <div className="space-y-2">
              <div className="flex justify-between text-sm font-semibold">
                <span>Install readiness</span>
                <span>{taskProgress}%</span>
              </div>
              <Progress value={taskProgress} />
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge variant="success">{stats.active_units} active</Badge>
              <Badge variant="secondary">
                {stats.completed_tasks}/{stats.total_tasks} tasks
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="onboarding" className="space-y-5">
          <TabsList>
            <TabsTrigger value="onboarding">Onboarding</TabsTrigger>
            <TabsTrigger value="units">Units</TabsTrigger>
            <TabsTrigger value="devices">Devices</TabsTrigger>
            <TabsTrigger value="activity">Activity</TabsTrigger>
          </TabsList>

          <TabsContent value="onboarding">
            <OnboardingPanel
              zones={zones.data ?? []}
              defaultClientId={defaultClientId}
              defaultProjectId={projectId}
              onSubmit={handleOnboard}
              isSubmitting={busy}
            />
          </TabsContent>

          <TabsContent value="units">
            <div className="grid gap-5 xl:grid-cols-2">
              {(units.data ?? []).length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center text-muted-foreground">
                    No units are mapped yet.
                  </CardContent>
                </Card>
              ) : (
                (units.data ?? []).map((unit) => (
                  <UnitChecklist
                    key={unit.id}
                    unit={unit}
                    isUpdating={mutations.updateTask.isPending}
                    onTaskToggle={(task, checked) =>
                      mutations.updateTask.mutate({
                        id: task.id,
                        payload: {
                          is_complete: checked,
                          completed_quantity: checked ? task.target_quantity : 0,
                        },
                      })
                    }
                  />
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="devices">
            <div className="space-y-5">
              <HubTable
                hubs={sortedHubs}
                isBusy={busy}
                onRefresh={(id) => mutations.refreshHub.mutate(id)}
              />
              <DeviceTable
                devices={sortedDevices}
                isBusy={busy}
                onRefresh={(id) => mutations.refreshDevice.mutate(id)}
                onOpen={(id) => mutations.openValve.mutate(id)}
                onClose={(id) =>
                  mutations.closeValve.mutate({
                    id,
                    notes: 'Closed from IronView smart valve console.',
                  })
                }
              />
            </div>
          </TabsContent>

          <TabsContent value="activity">
            <Card>
              <CardHeader>
                <CardTitle className="text-xl">Recent activity</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {(events.data ?? []).length === 0 ? (
                  <div className="rounded-[13px] border border-border bg-muted/40 p-6 text-center text-muted-foreground">
                    No smart valve events have been recorded yet.
                  </div>
                ) : (
                  (events.data ?? []).map((event) => (
                    <div
                      key={event.id}
                      className="flex gap-3 rounded-[13px] border border-border bg-muted/40 p-4"
                    >
                      <span className="mt-0.5 grid size-9 shrink-0 place-items-center rounded-[10px] bg-card text-primary">
                        {event.severity === 'critical' ? (
                          <AlertTriangle className="size-4" strokeWidth={1.9} />
                        ) : event.event_type.includes('sync') ? (
                          <Activity className="size-4" strokeWidth={1.9} />
                        ) : (
                          <CheckCircle2 className="size-4" strokeWidth={1.9} />
                        )}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold">{event.message}</div>
                        <div className="mt-1 text-xs text-muted-foreground">
                          {event.unit_label || event.device_name || event.source} ·{' '}
                          {formatDateTime(event.occurred_at)}
                        </div>
                      </div>
                      <Badge variant={event.severity === 'critical' ? 'destructive' : 'secondary'}>
                        {event.severity}
                      </Badge>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </BaseLayout>
  );
}
